/**
 * LES STATISTIQUES DES NC, CÔTÉ ÉCRAN — module PUR (RQ, 13/10/2026, lot 4).
 *
 * L'analyse fine du registre : où sont les écarts (nature), qui les trouve (le
 * laboratoire ou l'extérieur), et ce qui SE RÉPÈTE — même problème, même
 * appareil, même correspondant. ⚠️ Même règle que le moteur pour la revue de
 * direction (`backend/src/nc-statistiques.ts`) : une récurrence = au moins deux
 * NC sur la fenêtre, une valeur vide n'en est jamais une.
 * Testé depuis `backend/tests/front-stats-nc.test.js`.
 */

export interface NcPourStats {
  probleme?: string | null; equipementCode?: string | null; correspondantCode?: string | null;
  titre?: string | null; dateDeclaration?: string | null; dateDetection?: string | null;
}

const txt = (v: unknown) => String(v ?? '').trim();

/** Les NC déclarées (ou détectées) dans les `jours` qui précèdent `aujourdhui` (AAAA-MM-JJ). */
export function depuis<T extends NcPourStats>(ncs: readonly T[], aujourdhui: string, jours = 365): T[] {
  const fin = new Date(`${aujourdhui}T00:00:00Z`).getTime();
  return ncs.filter((n) => {
    const d = txt(n.dateDeclaration || n.dateDetection).slice(0, 10);
    if (!d) return false;
    const t = new Date(`${d}T00:00:00Z`).getTime();
    return t <= fin && t > fin - jours * 86400000;
  });
}

/** Les valeurs d'un champ revenues au moins `min` fois, de la plus fréquente à la moins. */
export function recurrences(ncs: readonly NcPourStats[], champ: 'probleme' | 'equipementCode' | 'correspondantCode', min = 2):
  { valeur: string; n: number; exemple: string }[] {
  const m = new Map<string, { n: number; exemple: string }>();
  for (const nc of ncs) {
    const v = txt(nc[champ]);
    if (!v) continue;
    const e = m.get(v) || { n: 0, exemple: txt(nc.titre) };
    e.n += 1;
    m.set(v, e);
  }
  return [...m.entries()].filter(([, e]) => e.n >= min)
    .map(([valeur, e]) => ({ valeur, n: e.n, exemple: e.exemple }))
    .sort((a, b) => b.n - a.n || a.valeur.localeCompare(b.valeur));
}

/* ═══════════════════════════════════════════════════════════════════════════
   LE MODULE « STATISTIQUES » (Ines, 20/10/2026 : « un module de génération de statistiques de NC avec possibilité
   d'envoi par correspondant »). La PÉRIODE est la même pour tout le laboratoire et pour un correspondant — et le
   serveur lit exactement les mêmes bornes (`backend/src/releve-correspondant.ts`, `normaliserPeriode`).
   ═══════════════════════════════════════════════════════════════════════════ */

export type ModeStats = 'mois' | 'trimestre' | 'annee' | 'plage';
export interface PeriodeStats { mode: ModeStats; mois: string; trimestre: string; annee: string; du: string; au: string }

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
export const moisEnClair = (m: string) => { const x = /^(\d{4})-(\d{2})$/.exec(m); return x ? `${MOIS_FR[Number(x[2]) - 1]} ${x[1]}` : m; };
const jourEnClair = (j: string) => { const x = /^(\d{4})-(\d{2})-(\d{2})$/.exec(j); return x ? `${x[3]}/${x[2]}/${x[1]}` : j; };
const dernierJour = (an: number, mo: number) => new Date(Date.UTC(an, mo, 0)).getUTCDate();
const moisAvant = (m: string) => { const an = Number(m.slice(0, 4)); const mo = Number(m.slice(5, 7)); return mo <= 1 ? `${an - 1}-12` : `${an}-${String(mo - 1).padStart(2, '0')}`; };
const trimestreDe = (jour: string) => `${jour.slice(0, 4)}-T${Math.floor((Number(jour.slice(5, 7)) - 1) / 3) + 1}`;

/** La période par défaut : le MOIS ÉCOULÉ (les bilans se font à la fin du mois), le trimestre et l'année en cours. */
export function periodeParDefaut(aujourdhui: string): PeriodeStats {
  const mois = moisAvant(aujourdhui.slice(0, 7));
  return { mode: 'mois', mois, trimestre: trimestreDe(aujourdhui), annee: aujourdhui.slice(0, 4), du: `${mois}-01`, au: aujourdhui };
}

/** Les bornes (incluses) et le libellé d'une période ; `null` si elle ne tient pas (plage inversée, champ vide). */
export function bornesStats(p: PeriodeStats): { du: string; au: string; libelle: string } | null {
  if (p.mode === 'mois') {
    if (!/^\d{4}-\d{2}$/.test(p.mois)) return null;
    const an = Number(p.mois.slice(0, 4)); const mo = Number(p.mois.slice(5, 7));
    return { du: `${p.mois}-01`, au: `${p.mois}-${String(dernierJour(an, mo)).padStart(2, '0')}`, libelle: moisEnClair(p.mois) };
  }
  if (p.mode === 'trimestre') {
    const x = /^(\d{4})-T([1-4])$/.exec(p.trimestre); if (!x) return null;
    const an = Number(x[1]); const premier = (Number(x[2]) - 1) * 3 + 1;
    return { du: `${an}-${String(premier).padStart(2, '0')}-01`, au: `${an}-${String(premier + 2).padStart(2, '0')}-${dernierJour(an, premier + 2)}`, libelle: `${x[2] === '1' ? '1ᵉʳ' : `${x[2]}ᵉ`} trimestre ${an}` };
  }
  if (p.mode === 'annee') return /^\d{4}$/.test(p.annee) ? { du: `${p.annee}-01-01`, au: `${p.annee}-12-31`, libelle: `année ${p.annee}` } : null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(p.du) || !/^\d{4}-\d{2}-\d{2}$/.test(p.au) || p.du > p.au) return null;
  return { du: p.du, au: p.au, libelle: `du ${jourEnClair(p.du)} au ${jourEnClair(p.au)}` };
}

/** La période précédente, de même nature : le mois, le trimestre, l'année d'avant ; pour une plage, autant de jours juste avant. */
export function periodePrecedenteStats(p: PeriodeStats): { du: string; au: string; libelle: string } | null {
  const b = bornesStats(p); if (!b) return null;
  if (p.mode === 'mois') return bornesStats({ ...p, mois: moisAvant(p.mois) });
  if (p.mode === 'trimestre') { const an = Number(p.trimestre.slice(0, 4)); const t = Number(p.trimestre.slice(6)); return bornesStats({ ...p, trimestre: t === 1 ? `${an - 1}-T4` : `${an}-T${t - 1}` }); }
  if (p.mode === 'annee') return bornesStats({ ...p, annee: String(Number(p.annee) - 1) });
  const jours = Math.round((Date.parse(`${b.au}T00:00:00Z`) - Date.parse(`${b.du}T00:00:00Z`)) / 86400000) + 1;
  const au = new Date(Date.parse(`${b.du}T00:00:00Z`) - 86400000); const du = new Date(au.getTime() - (jours - 1) * 86400000);
  return bornesStats({ ...p, mode: 'plage', du: du.toISOString().slice(0, 10), au: au.toISOString().slice(0, 10) });
}

/** Les mois de l'évolution : chaque mois de la période (24 au plus) ; pour un mois seul, les six derniers. */
export function moisEvolution(p: PeriodeStats): string[] {
  const b = bornesStats(p); if (!b) return [];
  if (p.mode === 'mois') { const l = [p.mois]; while (l.length < 6) l.unshift(moisAvant(l[0]!)); return l; }
  const out: string[] = []; let m = b.au.slice(0, 7);
  while (m >= b.du.slice(0, 7) && out.length < 24) { out.unshift(m); m = moisAvant(m); }
  return out;
}

/** Ce que le serveur lit de la période (URL ou corps) — un seul objet pour l'aperçu, le CSV, le PDF et le courriel. */
export function periodePourServeur(p: PeriodeStats): Record<string, string> {
  return p.mode === 'mois' ? { mode: 'mois', mois: p.mois } : p.mode === 'trimestre' ? { mode: 'trimestre', trimestre: p.trimestre }
    : p.mode === 'annee' ? { mode: 'annee', annee: p.annee } : { mode: 'plage', du: p.du, au: p.au };
}

/** Le jour d'une NC pour les statistiques : la détection d'abord, la déclaration à défaut (même règle que le relevé). */
export const jourStats = (n: NcPourStats) => txt(n.dateDetection || n.dateDeclaration).slice(0, 10);

/** Une NC entre aux statistiques si elle est RETENUE : ni au triage (« soumise »), ni annulée — comme au relevé. */
export const retenueStats = (n: { statut?: string | null }) => !['soumise', 'annulee'].includes(txt(n.statut));

/** Les NC retenues dont le jour tombe entre deux bornes incluses. */
export function ncsEntre<T extends NcPourStats & { statut?: string | null }>(ncs: readonly T[], du: string, au: string): T[] {
  return ncs.filter((n) => { const j = jourStats(n); return retenueStats(n) && !!j && j >= du && j <= au; });
}
